/**
 * GA4 Analytics Configuration
 * Centralized event names and parameters for consistent tracking
 */

/**
 * Standard Event Names
 * Following GA4 naming conventions:
 * - Use lowercase with underscores
 * - Be descriptive but concise
 * - Group related events with common prefix
 */
export const AnalyticsEvents = {
  // Location Profile Events
  PROFILE_OPENED: 'profile_opened',
  PROFILE_CLOSED: 'profile_closed',
  PROFILE_LOADED: 'profile_loaded',
  PROFILE_TAB_CHANGED: 'profile_tab_changed',
  PROFILE_DOWNLOADED: 'profile_downloaded',
  PROFILE_DROPDOWN_CHANGED: 'profile_dropdown_changed',
  
  // Map Events
  MAP_MARKER_CLICKED: 'map_marker_clicked',
  MAP_ZOOM_CHANGED: 'map_zoom_changed',
  MAP_PAN: 'map_pan',
  MAP_LAYER_TOGGLED: 'map_layer_toggled',
  
  // Dataset Events
  DATASET_SELECTED: 'dataset_selected',
  DATASET_FILTERED: 'dataset_filtered',
  DATASET_EXPORTED: 'dataset_exported',
  DATASET_VIEW_SWITCHED: 'dataset_view_switched',
  DATASET_UPLOADED: 'dataset_uploaded',
  DATASET_DELETED: 'dataset_deleted',
  DATASET_DOWNLOADED: 'dataset_downloaded',
  
  // Overlay Events
  OVERLAY_ENABLED: 'overlay_enabled',
  OVERLAY_DISABLED: 'overlay_disabled',
  OVERLAY_CONFIGURED: 'overlay_configured',
  
  // External Data Events
  EXTERNAL_DATA_FETCHED: 'external_data_fetched',
  EXTERNAL_DATA_REFRESHED: 'external_data_refreshed',
  
  // Alert Events
  ALERTS_PANEL_OPENED: 'alerts_panel_opened',
  
  // Feature Navigation Events
  FEATURE_ACCESSED: 'feature_accessed',
  FEATURE_EXITED: 'feature_exited',
  
  // Search Events
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  
  // User Actions
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  USER_SIGNUP: 'user_signup',
  
  // Error Events
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  
  // Performance Events
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time'
} as const;

/**
 * Standard Event Parameters
 * Consistent parameter names across all events
 */
export const AnalyticsParams = {
  // Entity IDs
  DATASET_ID: 'dataset_id',
  DATAPOINT_ID: 'datapoint_id',
  OVERLAY_ID: 'overlay_id',
  USER_ID: 'user_id',
  ACCOUNT_ID: 'account_id',
  
  // Profile Parameters
  PROFILE_TYPE: 'profile_type', // 'location' or 'overlay'
  PROFILE_TAB: 'profile_tab', // 'INTERNAL', 'EXTERNAL', 'CLIMATE', etc.
  PROFILE_SECTION: 'profile_section',
  
  // Map Parameters
  ZOOM_LEVEL: 'zoom_level',
  CENTER_LAT: 'center_lat',
  CENTER_LNG: 'center_lng',
  LAYER_NAME: 'layer_name',
  
  // Dataset Parameters
  DATASET_NAME: 'dataset_name',
  DATASET_TYPE: 'dataset_type',
  FILTER_TYPE: 'filter_type',
  EXPORT_FORMAT: 'export_format',
  VIEW_MODE: 'view_mode',
  RECORD_COUNT: 'record_count',
  SELECTION_COUNT: 'selection_count',
  
  // Search Parameters
  SEARCH_QUERY: 'search_query',
  SEARCH_RESULTS_COUNT: 'results_count',
  
  // Error Parameters
  ERROR_TYPE: 'error_type',
  ERROR_MESSAGE: 'error_message',
  ERROR_CODE: 'error_code',
  
  // Performance Parameters
  DURATION_MS: 'duration_ms',
  ENDPOINT: 'endpoint',
  
  // External Data Parameters
  EXTERNAL_DATA_SOURCE: 'external_data_source',
  IS_REFRESH: 'is_refresh',
  
  // Generic Parameters
  VALUE: 'value',
  LABEL: 'label',
  CATEGORY: 'category',
  ACTION: 'action'
} as const;

/**
 * Profile Tab Types
 */
export enum ProfileTab {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
  CLIMATE = 'climate',
  FORMULA = 'formula',
  ATTACHMENTS = 'attachments'
}

/**
 * Profile Types
 */
export enum ProfileType {
  LOCATION = 'location',
  OVERLAY = 'overlay'
}

/**
 * Export Formats
 */
export enum ExportFormat {
  PDF = 'pdf',
  XLSX = 'xlsx',
  CSV = 'csv',
  JSON = 'json'
}

/**
 * View Modes
 */
export enum ViewMode {
  MAP = 'map',
  TABLE = 'table',
  DASHBOARD = 'dashboard'
}

/**
 * Error Types
 */
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  UNKNOWN = 'unknown'
}

/**
 * Helper function to create standardized event parameters
 */
export class AnalyticsHelper {
  /**
   * Create profile event parameters
   */
  static profileParams(
    datasetId: string,
    datapointId?: string,
    additionalParams?: Record<string, any>
  ): Record<string, any> {
    return {
      [AnalyticsParams.DATASET_ID]: datasetId,
      [AnalyticsParams.DATAPOINT_ID]: datapointId || 'unknown',
      ...additionalParams
    };
  }

  /**
   * Create dataset event parameters
   */
  static datasetParams(
    datasetId: string,
    datasetName?: string,
    additionalParams?: Record<string, any>
  ): Record<string, any> {
    return {
      [AnalyticsParams.DATASET_ID]: datasetId,
      [AnalyticsParams.DATASET_NAME]: datasetName || 'unknown',
      ...additionalParams
    };
  }

  /**
   * Create map event parameters
   */
  static mapParams(
    zoomLevel?: number,
    centerLat?: number,
    centerLng?: number,
    additionalParams?: Record<string, any>
  ): Record<string, any> {
    return {
      [AnalyticsParams.ZOOM_LEVEL]: zoomLevel,
      [AnalyticsParams.CENTER_LAT]: centerLat,
      [AnalyticsParams.CENTER_LNG]: centerLng,
      ...additionalParams
    };
  }

  /**
   * Create error event parameters
   */
  static errorParams(
    errorType: ErrorType,
    errorMessage: string,
    errorCode?: string,
    additionalParams?: Record<string, any>
  ): Record<string, any> {
    return {
      [AnalyticsParams.ERROR_TYPE]: errorType,
      [AnalyticsParams.ERROR_MESSAGE]: errorMessage,
      [AnalyticsParams.ERROR_CODE]: errorCode,
      ...additionalParams
    };
  }

  /**
   * Create performance event parameters
   */
  static performanceParams(
    durationMs: number,
    endpoint?: string,
    additionalParams?: Record<string, any>
  ): Record<string, any> {
    return {
      [AnalyticsParams.DURATION_MS]: durationMs,
      [AnalyticsParams.ENDPOINT]: endpoint,
      ...additionalParams
    };
  }

  /**
   * Normalize tab name to lowercase
   */
  static normalizeTabName(tab: string): string {
    return tab?.toLowerCase() || 'unknown';
  }

  /**
   * Sanitize parameter values
   * - Remove sensitive data
   * - Truncate long strings
   * - Convert to appropriate types
   */
  static sanitizeParams(params: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(params)) {
      // Skip null/undefined
      if (value === null || value === undefined) {
        continue;
      }
      
      // Truncate long strings
      if (typeof value === 'string' && value.length > 100) {
        sanitized[key] = value.substring(0, 97) + '...';
      }
      // Convert numbers
      else if (typeof value === 'number') {
        sanitized[key] = value;
      }
      // Convert booleans
      else if (typeof value === 'boolean') {
        sanitized[key] = value;
      }
      // Convert other types to string
      else {
        sanitized[key] = String(value);
      }
    }
    
    return sanitized;
  }
}
